<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_SCROLLABLE_PADDING_MOBILE : MODAL_SCROLLABLE_PADDING_DESKTOP"
                   maxWidth="720px"
                   maxHeight="600px"
                   hasScrollTopShadow
                   hasScrollBottomShadow
                   useClickOutsideClose
                   @close="handleClose">
            <template #header>
                <div class="general-modal__header-container">
                    <div class="general-modal__title">
                        {{ userStore.tangible?.questMissionTitle }}
                    </div>
                    <div class="general-modal__subtitle">
                        <Translate translationKey="quests-dashboard.tangibleRewardModalSubtitle"
                                   :replacements="{ reward: userStore.tangible?.rewardTitle }"
                                   fallback="You've successfully cracked this Mission, and as a result, you've won :reward" /> 🎉
                    </div>
                </div>
            </template>
            <template #content>
                <div class="general-modal__content general-modal__content--alt">
                    <div v-if="isCompleted"
                         class="tangible-reward-over">
                        <Translate translationKey="quests-dashboard.tangibleRewardClaimed"
                                   fallback="Tangible reward claimed" />
                    </div>
                    <div v-else-if="isExpired"
                         class="tangible-reward-over">
                        <Translate translationKey="quests-dashboard.tangibleRewardExpired"
                                   fallback="Tangible reward expired" />
                    </div>
                    <template v-else>
                        <div class="tangible-reward-info">
                            <p>To claim your prize, please provide your <b>full postal address</b> through this form, including:</p>
                            <ul>
                                <li><b>Street name</b></li>
                                <li><b>House/apartment number</b></li>
                                <li><b>Province/region</b></li>
                                <li><b>Country</b></li>
                                <li><b>Postal code</b></li>
                            </ul>
                            <p><b>Important Notes:</b></p>
                            <ul>
                                <li>The form must be filled out within <b>three days</b> of receiving this message.</li>
                                <li>If we don’t receive your complete details within this time, the prize will be passed on to another winner.</li>
                            </ul>
                            <p><b>Disclaimer:</b></p>
                            <p>
                                Deliveries are restricted to <b>Afghanistan, Belarus, Bhutan, Ascension Island, Yemen,
                                    Martinique, South Georgia and the South Sandwich Islands, South Sudan, Timor-Leste, Russia,
                                    Syria, Saint Helena, and Tristan da Cunha</b>.
                            </p>
                        </div>
                        <div class="field">
                            <div class="field__title">
                                <Translate translationKey="quests-dashboard.fullName"
                                           fallback="Full name" />
                            </div>
                            <input v-model="name"
                                   type="text"
                                   class="field__input"
                                   :placeholder="getTrans('quests-dashboard.enterFullName', 'Enter your full name')">
                            <div v-for="(error, index) in backendErrors.name"
                                 :key="index"
                                 class="field__error-message">
                                {{ error }}
                            </div>
                        </div>
                        <div class="field">
                            <div class="field__title">
                                <Translate translationKey="quests-dashboard.phoneNumber"
                                           fallback="Phone number" />
                            </div>
                            <input v-model="phone"
                                   type="tel"
                                   class="field__input"
                                   :class="!isPhoneValid ? 'field__input--error' : ''"
                                   :placeholder="getTrans('quests-dashboard.enterPhoneNumber', 'Enter your phone number')">
                            <div v-for="(error, index) in backendErrors.phone"
                                 :key="index"
                                 class="field__error-message">
                                {{ error }}
                            </div>
                        </div>
                        <div class="field">
                            <div class="field__title">
                                <Translate translationKey="quests-dashboard.fullPostalAddress"
                                           fallback="Full postal address" />
                            </div>
                            <textarea v-model="address"
                                      class="field__input field__input--textarea"
                                      cols="40"
                                      :placeholder="getTrans('quests-dashboard.reporting.enterFullPostalAddressHere', 'Enter your full postal address')"
                                      rows="4"
                                      maxlength="500" />
                            <div v-for="(error, index) in backendErrors.address"
                                 :key="index"
                                 class="field__error-message">
                                {{ error }}
                            </div>
                        </div>
                        <div class="field">
                            <div v-if="backendError"
                                 class="field__error-message field__error-message--general">
                                {{ backendError }}
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <template #footer>
                <div v-if="!isCompleted && !isExpired"
                     class="general-modal__actions general-modal__actions--alt">
                    <span class="general-modal__action general-modal__action--confirm">
                        <BaseButton :disabled="isSubmitDisabled"
                                    @click="onRewardDataSend">
                            <Translate translationKey="quests-dashboard.submit"
                                       fallback="Submit" />
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import {
        computed, ref,
    } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { getTrans } from '@/js/helpers/getTrans';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import {
        MODAL_SCROLLABLE_PADDING_DESKTOP,
        MODAL_SCROLLABLE_PADDING_MOBILE,
    } from '@/js/quests/global/constants/modal';
    import { useFormErrorHandling } from '@/js/quests/global/composables/useFormErrorHandling';

    const modalStore = useModalStore();
    const userStore = useUserStore();
    const { isMobileOrTablet } = useDeviceSize();
    const {
        backendErrors, backendError, setErrors, clearErrors,
    } = useFormErrorHandling();

    const name = ref('');
    const address = ref('');
    const phone = ref('');

    const isPhoneValid = computed(() => {
        if (!phone.value) {
            return true;
        }

        const phoneRegex = /^\+[1-9]\d{1,14}$/;

        return phoneRegex.test(phone.value);
    });

    const isSubmitDisabled = computed(() => !name.value || (!phone.value || !isPhoneValid.value) || address.value === '');

    function isTimePastDate(targetDateString: string) {
        const currentTime = new Date().getTime();
        const targetTime = new Date(targetDateString).getTime();

        return currentTime > targetTime;
    }

    const isExpired = computed(() => isTimePastDate(userStore.tangible?.expiresAt));
    const isCompleted = computed(() => !!userStore.tangible?.completedAt);

    const handleClose = () => {
        modalStore.toggleModal(ModalEnum.TANGIBLE_REWARD, false);
    };

    const onRewardDataSend = async () => {
        clearErrors();

        try {
            await userStore.submitTangibleRewardData(userStore.tangible.id, {
                name: name.value,
                address: address.value,
                phone: phone.value,
            });

            handleClose();
        } catch (error) {
            setErrors(error);
        }
    };

    useModalActive();
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__subtitle {
        color: variables.$baseWhite;
    }
}

.field {
    @include modalMixins.modal-field;
    & {
        width: 100%;
    }

    &__input--textarea {
        height: 130px;
    }
}

.tangible-reward-over {
    color: variables.$baseWhite;
    text-align: center;
}

.tangible-reward-info {
    margin-top: 8px;
    font-size: 14px;

    ul {
        padding-left: 24px;
    }
}
</style>
